import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { login } from '../services/users';
import { getCurrentUser } from '../services/auth';
import { useElementLoader } from '../utils/loader';
import LanguageSwitcher from '../components/languageSwitcher';
import './auth.css';

const LoginPage = () => {

    const { t } = useTranslation();
    const [searchParams] = useSearchParams()
    const [username, setUsername] = useState()
    const [password, setPassword] = useState()
    // ?suspended=1: signed out because the company was suspended mid-session (services/http.js).
    const [error, setError] = useState(() => searchParams.get('suspended') ? t('auth.companySuspended') : undefined)
    const [isLoading, setIsLoading] = useState(false)
    const [loader] = useElementLoader()

    let navigate = useNavigate();

    const handleLogin = async () => {

        setError()
        setIsLoading(true)

        let account = {
            username,
            password
        }

        try {
            const { data: { code, message } } = await login(account)

            if (code === 200) {
                localStorage.setItem('cleaning-crm-token', message.token)
                const user = getCurrentUser()
                navigate(user?.isPlatformOwner ? '/platform' : '/home')
            } else if (code === 403 && message && message.needsPasswordReset) {
                // Account from before the new sign-in: its old password can't
                // be checked, so the user sets a new one through Forgot
                // password — the e-mailed link also confirms their address.
                setError({ needsPasswordReset: true })
            } else if (code === 403 && message && message.needsPasswordSetup) {
                // Account exists but has no password yet (invitation not
                // accepted, or the account predates hashed passwords). A
                // set-password link has been e-mailed.
                setError(t('auth.needsPasswordSetup', { email: message.email || t('auth.yourEmailFallback') }))
            } else if (code === 403 && message && message.companySuspended) {
                // The company is suspended or its trial ended — no token is
                // issued, so say why here instead of failing every page later.
                setError(message.reason === 'trialExpired' ? t('auth.companyTrialExpired') : t('auth.companySuspended'))
            } else if (code === 403 && message && message.needsVerification) {
                // Account exists but email isn't verified — show an error
                // instead of redirecting to the resend/verification screen.
                setError(t('auth.emailNotVerified'))
            } else {
                setError(typeof message === 'string' ? message : t('auth.loginFailed'))
            }
        } catch (e) {
            setError(e.message)
        }

        setIsLoading(false)
    }

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            handleLogin()
        }
    }

    return (
        <div className="auth-page">
            <div className="auth-shell">

                {/* ───────── LEFT / BRAND PANEL ───────── */}
                <aside className="auth-brand-panel">
                    <div className="auth-brand-top">
                        <Link to="/login" className="auth-brand-link" aria-label={t('auth.logoGoToSignIn')}>
                            <img
                                src={process.env.PUBLIC_URL + '/logo-soroby.svg'}
                                alt="Soroby"
                                className="auth-brand-logo"
                            />
                        </Link>
                    </div>

                    <div className="auth-brand-message">
                        <h1 className="auth-brand-title" dangerouslySetInnerHTML={{ __html: t('auth.brandTitle') }} />
                        <p className="auth-brand-tagline">
                            {t('auth.brandTagline')}
                        </p>
                    </div>

                    <div className="auth-brand-footer">
                        <span>© {new Date().getFullYear()} Soroby · {t('auth.builtForServiceBusinesses')}</span>
                        <span>soroby.com</span>
                    </div>
                </aside>

                {/* ───────── RIGHT / FORM PANEL ───────── */}
                <main className="auth-form-panel">
                    <LanguageSwitcher className="auth-lang" id="auth-language-drop" drop="down" align="end" showIcon />

                    <div className="auth-form-inner">

                        <div className="auth-mobile-brand">
                            <Link to="/login" className="auth-brand-link" aria-label={t('auth.logoGoToSignIn')}>
                                <img src={process.env.PUBLIC_URL + '/logo-soroby.svg'} alt="Soroby" />
                            </Link>
                        </div>

                        <div className="auth-form-heading">
                            <h1 className="">{t('auth.welcomeBack')}</h1>
                            <p>{t('auth.signInToWorkspace')}</p>
                        </div>

                        <div className="auth-form">
                            <div className="auth-field">
                                <label className="auth-label" htmlFor="login-username">{t('auth.username')}</label>
                                <div className="auth-input-wrap">
                                    <svg className="auth-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                        <circle cx="12" cy="7" r="4" />
                                    </svg>
                                    <input
                                        id="login-username"
                                        className="auth-input"
                                        type="text"
                                        autoComplete="username"
                                        placeholder="your.handle"
                                        disabled={isLoading}
                                        onChange={(e) => setUsername(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                    />
                                </div>
                            </div>

                            <div className="auth-field">
                                <div className="auth-label-row">
                                    <label className="auth-label" htmlFor="login-password">{t('auth.password')}</label>
                                    <Link to="/forgot-password" className="auth-link-quiet">{t('auth.forgotPassword')}</Link>
                                </div>
                                <div className="auth-input-wrap">
                                    <svg className="auth-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                    </svg>
                                    <input
                                        id="login-password"
                                        className="auth-input"
                                        type="password"
                                        autoComplete="current-password"
                                        placeholder="••••••••"
                                        disabled={isLoading}
                                        onChange={(e) => setPassword(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                    />
                                </div>
                            </div>

                            <button
                                type="button"
                                className="auth-btn"
                                disabled={isLoading}
                                onClick={() => handleLogin()}
                            >
                                {isLoading ? loader : (
                                    <>
                                        <span>{t('auth.signIn')}</span>
                                        <span className="arrow" aria-hidden="true">→</span>
                                    </>
                                )}
                            </button>

                            {error &&
                                <div className="auth-error">
                                    {error.needsPasswordReset
                                        ? <>
                                            {t('auth.needsPasswordReset')}{' '}
                                            <Link to={`/forgot-password${username && username.trim() ? `?email=${encodeURIComponent(username.trim())}` : ''}`}>
                                                {t('auth.setNewPassword')}
                                            </Link>
                                        </>
                                        : typeof error === 'string' ? error : t('auth.loginFailed')}
                                </div>
                            }
                        </div>

                        <p className="auth-secondary">
                            {t('auth.newToSoroby')} <Link to="/register">{t('auth.createAnAccount')}</Link>
                        </p>
                    </div>
                </main>

            </div>
        </div>
    )
}

export default LoginPage;
